import { useEffect, useMemo, useState } from "react";
import Select from "react-select";

import { get, post } from "../../services/api";
import AdminSection from "../../components/AdminSection/AdminSection";
import NotifierPopup from "../../components/NotifierPopup/NotifierPopup";

const selectStyle = {
  control: (base) => ({
    ...base,
    background: "unset",
    borderColor: "#DCDCDC",
    borderRadius: "4px",
    minWidth: 180
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 })
};

const nomeMeses = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

function isPeriodoPermitidoFrontend(mes, ano) {
  if (!mes || !ano) return false;

  const hoje = new Date();
  const periodoAtual = hoje.getFullYear() * 12 + (hoje.getMonth() + 1);

  const dataMesPassado = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  const periodoMesPassado = dataMesPassado.getFullYear() * 12 + (dataMesPassado.getMonth() + 1);

  const periodoSelecionado = Number(ano) * 12 + Number(mes);

  return periodoSelecionado >= periodoMesPassado;
}

export default function MetaVendedorGlobalAdm() {
  const [notify, setNotify] = useState({
    isActive: false,
    isSuccessful: undefined,
    message: undefined
  });

  const [mes, setMes] = useState(null);
  const [ano, setAno] = useState(null);
  const [valorMeta, setValorMeta] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const anoAtual = new Date().getFullYear();

  const optionsAno = useMemo(() => {
    return Array.from({ length: 8 }, (_, i) => {
      const value = anoAtual - 1 + i;
      return { value, label: String(value) };
    });
  }, [anoAtual]);

  const optionsMes = useMemo(() => {
    return nomeMeses.map((mesNome, index) => ({
      value: index + 1,
      label: mesNome
    }));
  }, []);

  useEffect(() => {
    const dataAtual = new Date();
    setMes({ value: dataAtual.getMonth() + 1, label: nomeMeses[dataAtual.getMonth()] });
    setAno({ value: dataAtual.getFullYear(), label: String(dataAtual.getFullYear()) });
  }, []);

  const setNotifier = (isSuccess, message) => {
    setNotify({
      isActive: true,
      isSuccessful: isSuccess,
      message
    });
  };

  const limparValor = () => setValorMeta("");

  const fetchMeta = async () => {
    if (!mes?.value || !ano?.value) {
      setNotifier(false, "Selecione mês e ano.");
      return;
    }

    setIsLoading(true);
    try {
      const adminToken = localStorage.getItem("adminToken");
      const response = await get(
        "/admin/gerenciarMetasExtra/meta-vendedor-global",
        { params: { mes: mes.value, ano: ano.value } },
        adminToken
      );

      if (response && typeof response.valor_meta_vendedor !== "undefined" && response.valor_meta_vendedor !== null) {
        setValorMeta(String(response.valor_meta_vendedor).replace(".", ","));
      } else {
        setValorMeta("");
        setNotifier(true, "Nenhuma meta cadastrada para o período. Informe um valor para salvar.");
      }
    } catch (error) {
      setNotifier(false, "Erro ao buscar meta vendedor global.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSalvar = async (event) => {
    event.preventDefault();

    if (!mes?.value || !ano?.value) {
      setNotifier(false, "Selecione mês e ano.");
      return;
    }

    if (!isPeriodoPermitidoFrontend(mes.value, ano.value)) {
      setNotifier(false, "Somente mês passado, atual e futuros podem ser alterados.");
      return;
    }

    const valorNormalizado = Number(String(valorMeta).replace(/\./g, "").replace(",", "."));
    if (!Number.isFinite(valorNormalizado)) {
      setNotifier(false, "Informe um valor numérico válido.");
      return;
    }

    setIsSaving(true);
    try {
      const adminToken = localStorage.getItem("adminToken");
      const payload = {
        mes: mes.value,
        ano: ano.value,
        valor_meta_vendedor: valorNormalizado
      };

      const { response } = await post(
        "/admin/gerenciarMetasExtra/meta-vendedor-global",
        payload,
        adminToken,
        { headers: { "Content-Type": "application/json" } }
      );

      if (response?.status >= 400) {
        const message = response?.data?.error || "Erro ao salvar meta vendedor global.";
        setNotifier(false, message);
        return;
      }

      setNotifier(true, "Meta vendedor global salva com sucesso.");
      await fetchMeta();
    } catch (error) {
      setNotifier(false, "Erro ao salvar meta vendedor global.");
    } finally {
      setIsSaving(false);
    }
  };

  const periodoValido = isPeriodoPermitidoFrontend(mes?.value, ano?.value);
  const podeSalvar = periodoValido && valorMeta !== "" && !isSaving;

  return (
    <>
      {notify.isActive && (
        <NotifierPopup notify={notify} setNotify={setNotify} />
      )}

      <AdminSection etapa={1} titulo={"Selecione o período"}>
        <div className="admin-filtros-dividos p-8">
          <div className="admin-filtros-left">
            <div>
              <label className="admin-label" htmlFor="mes">Mês</label>
              <Select
                name="mes"
                className="react-select"
                classNamePrefix="react-select"
                options={optionsMes}
                value={mes}
                onChange={setMes}
                placeholder="Mês"
                styles={selectStyle}
                isSearchable={false}
                menuPortalTarget={document.body}
              />
            </div>

            <div>
              <label className="admin-label" htmlFor="ano">Ano</label>
              <Select
                name="ano"
                className="react-select"
                classNamePrefix="react-select"
                options={optionsAno}
                value={ano}
                onChange={setAno}
                placeholder="Ano"
                styles={selectStyle}
                isSearchable={false}
                menuPortalTarget={document.body}
              />
            </div>

            <div className="flex items-end gap-2">
              <button
                type="button"
                className="section-button"
                onClick={fetchMeta}
                disabled={isLoading || !mes?.value || !ano?.value}
              >
                {isLoading ? "Buscando..." : "Buscar"}
              </button>
            </div>
          </div>
        </div>
      </AdminSection>

      <AdminSection etapa={2} titulo={"Meta Vendedor Global"} disabled={!mes?.value || !ano?.value}>
        <form style={{ padding: "24px" }} onSubmit={handleSalvar}>
          <div className="flex flex-col gap-2" style={{ maxWidth: 320 }}>
            <label className="admin-label" htmlFor="valorMeta">Valor da meta</label>
            <input
              id="valorMeta"
              type="text"
              value={valorMeta}
              onChange={(e) => setValorMeta(e.target.value)}
              placeholder="Ex.: 10000,00"
              className="border border-gray-300 rounded px-3 py-2"
            />
            {!periodoValido && (
              <span style={{ color: "#E10F27", fontSize: 13 }}>
                Período bloqueado. Só é permitido alterar mês passado, atual e futuros.
              </span>
            )}
          </div>

          <div className="flex justify-end mt-6">
            <button type="button" className="admin-deny-btn" onClick={limparValor}>
              Limpar
            </button>
            <button type="submit" className="admin-accept-btn" disabled={!podeSalvar}>
              {isSaving ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </form>
      </AdminSection>
    </>
  );
}